import React, { FC, useEffect, useMemo, useState, useCallback } from 'react';
import { TagItemViewImage, TagItemViewWrapper } from './styles';
import { useTheme } from "styled-components";
import { useTranslation } from 'react-i18next';
import { Link } from "../../../shared/components/link";
import { CreateLinkTo } from "../../../shared/constants/linkTo";
import { useRouteMatch } from 'react-router-dom';
import { IPureTagElementsAfter, MasterTag, IMasterTagTypes, IPureTagAppearance } from '@brg/ui';
import { useGetSelectedTagColor } from "../../../shared/hooks/useGetSelectedTagColor";
import { useSelector } from 'react-redux';
import { TagCategoriesSelector } from 'shared/reducers/Tags';
import { API } from '@brg/api';
import useAppTester from 'shared/MobileMenu/hooks/useAppTester';

/**
 * Свойства Burgundy Tag
 */
export interface BTagProps {
  image?: string | JSX.Element;
  selected?: boolean;
  disabled?: boolean;
  parent?: boolean;
  removable?: boolean;
  secondary?: boolean;
  educational?: boolean;
  organization?: boolean;
  onClick?: (ev: any) => void;
  variant?: string;
  opacity?: boolean;
  isCoauthorTag?: boolean;
  isProjectsTag?: boolean;
  isShowMoreTag?: boolean;
  isInfoTags?: boolean;
  onIconClick?: (ev: any) => void;
  isRequired?: boolean;
  onlyWhiteTags?: boolean;
  isCompetitionsTitleTag?: boolean;
  isEducationCategory?: boolean;
  tag?: any;
  isShort?: boolean;
  isDeziiignStyle?: boolean;
  inactive?: boolean;
  importantColor?: string;
  isTagsCloud?: boolean;
  showRemoveIcon?: boolean;
  hasLink?: boolean;
  isFolder?: boolean;
  isCreateAction?: boolean;
  isCompetitionsStyleTag?: boolean;
  tagCategories?: string;
  icon?: IPureTagElementsAfter;
  noTooltip?: boolean;
  editIconClick?: any;
  colorTheme?: any;
}

/**
 * Burgundy Tag
 *
 * @param image     - изображение если нужно
 * @param children  - название тега (текст)
 * @param selected  - выбран или нет
 * @param disabled  - выключен
 * @param parent    - имеет потомков
 * @param removable - можно удалить
 * @param secondary - вторичный
 * @param onClick   событие при клике
 * @param variant - вариант дизайна
 * @param educational - является ли тег образовательным
 * @param organization - является ли тег тегом организации
 * @param opacity - прозрачность текста
 * @param isProjectsTag - Тег выбранный при редактировании проекта
 * @param isShowMoreTag - Тег в раскрывающемся showmore
 * @param isInfoTags - Теги, которые отображаются в модалке при ховере на табы
 * @param isCoauthorTag - Тег в дропдауне выбора соавторов в проекте
 * @param isRequired - Обязательный тег в данном контексте
 * @param onlyWhiteTags - Все теги должны быть белые
 * @param isCompetitionsTitleTag - тег в названии конкурса
 * @param isEducationCategory - текущая вкладка = "Образовательные теги"
 * @param isShort - тег небольшой
 * @param importantColor - принудительный цвет
 * @param isFolder - тег-папка в профиле
 * @param isCreateAction - кнопка псевдо-тег
 * @param tagCategories - категории к которым принадлежит тег
 * @constructor
 */
export const BTag: FC<React.PropsWithChildren<BTagProps>> = ({
  image,
  children,
  selected,
  disabled,
  removable,
  secondary,
  educational,
  isCreateAction,
  organization,
  onClick,
  variant,
  isCoauthorTag,
  isProjectsTag,
  isShowMoreTag,
  isRequired,
  isFolder,
  isEducationCategory,
  inactive,
  showRemoveIcon,
  onIconClick,
  tag,
  isTagsCloud,
  hasLink,
  tagCategories,
  icon,
  noTooltip,
  editIconClick,
  colorTheme
}) => {
  const { isDarkText } = useTheme();
  const { t, i18n: { language } } = useTranslation();

  const tagCategoriesIndex = useSelector(TagCategoriesSelector);

  const {
    isCompetition,
    isAcademy,
  } = useAppTester();

  const currentCategories = useMemo(() => {
    if (!tagCategories && !!tag?.groupIds?.length && !!tagCategoriesIndex?.size) {
      return tag.groupIds.reduce((acc: string[], groupId: string) => {
        const category = tagCategoriesIndex.get(groupId);
        if (category) {
          acc.push(API.toLang(category, language)?.name || '');
        }
        return acc;
      }, []).join(', ');
    }

    return tagCategories;
  }, [tagCategoriesIndex, tag?.groupIds, tagCategories, language]);

  const educationTip = useCallback(() => {
    const str = [];
    if (isEducationCategory) {
      str.push(t('designer_edit.tips.education_tag'));
    }
    if (isRequired) {
      str.push(t('designer_edit.tips.required_tag'));
    }
    if (!isEducationCategory) {
      str.push(t('designer_edit.tips.main_tag'));
    }

    return str.map((s, idx) => {
      const indent = ` ${s} `;
      return idx > 0 ? indent.toLowerCase() : indent;
    }).join(',');
  }, [isEducationCategory, isRequired, t]);

  const tooltipProps = useMemo(() => {
    if (noTooltip) return {};

    const baseProps = {
      'data-type': isDarkText ? 'dark' : 'light',
      'data-for': 'tooltip-parent-element'
    };

    if (isShowMoreTag) {
      return {
        ...baseProps,
        'data-tip': tag?.name || ''
      };
    }

    if (educational) {
      return {
        ...baseProps,
        'data-tip': currentCategories || educationTip(),
        'data-education': 'true'
      };
    }

    if (organization) {
      return {
        ...baseProps,
        'data-tip': t('designer_edit.tips.team_tag'),
        'data-unit': 'true'
      };
    }

    if (currentCategories || tag?.groupIds?.length > 0) {
      return {
        ...baseProps,
        'data-tip': currentCategories,
        'data-default': 'true'
      };
    }

    if (isFolder && isCreateAction) {
      return {
        ...baseProps,
        'data-tip': t('designer_edit.tips.team_tag'),
        'data-unit': 'true'
      };
    }

    return {};
  }, [noTooltip, isDarkText, isShowMoreTag, tag?.name, educational, currentCategories, educationTip, organization, t, tag?.groupIds, isFolder, isCreateAction]);

  const selectedColor = useGetSelectedTagColor(isFolder || false);
  const isFilmRoute = !!useRouteMatch('/film');

  const to = useMemo(() => {
    if (isFilmRoute) {
      return '/film' + CreateLinkTo.tag(tag?.tagId);
    }
    return CreateLinkTo.tag(tag?.tagId);
  }, [isFilmRoute, tag?.tagId]);

  const elementAfter = useMemo(() => {
    if (icon) {
      return { type: IPureTagElementsAfter[icon], extraProps: { onClick: onIconClick } };
    }

    if (showRemoveIcon) {
      return { type: IPureTagElementsAfter.close, extraProps: { onClick: onIconClick } };
    }

    return undefined;
  }, [icon, showRemoveIcon, onIconClick]);

  const masterTagType = useMemo(() => {
    if (educational) {
      return IMasterTagTypes.educational;
    }
    if (organization) {
      return IMasterTagTypes.organization;
    }
    if (isFolder) {
      return IMasterTagTypes.folder;
    }
    return IMasterTagTypes.simple;
  }, [educational, organization, isFolder]);

  if (image) {
    return (
      <TagItemViewWrapper
        {...tooltipProps}
        secondary={secondary}
        onClick={onClick}
        selected={selected}
        disabled={disabled}
        removable={removable}
        isCoauthorTag={isCoauthorTag}
        variant={variant}
        educational={educational}
        organization={organization}
        inactive={inactive}
        isAcademy={isAcademy}
        isCompetition={isCompetition}
        isTagsCloud={isTagsCloud}
      >
        {typeof image === 'string' ? (
          <TagItemViewImage image={image} />
        ) : (
          <TagItemViewImage image="">{image}</TagItemViewImage>
        )}
        {children}
      </TagItemViewWrapper>
    );
  }

  return (
    <Link to={to} disabled={!tag?.tagId || !hasLink} type="tag">
      <MasterTag
        {...tooltipProps}
        onClick={onClick}
        active={selected}
        disabled={disabled}
        type={masterTagType}
        elementAfter={elementAfter}
        activeColorTheme={selectedColor}
        editIconClick={editIconClick}
        {...(colorTheme ? { colorTheme, appearance: IPureTagAppearance.filled } : {})}
      >
        {children}
      </MasterTag>
    </Link>
  );
};
import React, { FC, useEffect, useMemo, useState, useCallback } from 'react';
import { TagItemViewImage, TagItemViewWrapper } from './styles';
import { useTheme } from "styled-components";
import { useTranslation } from 'react-i18next';
import { Link } from "../../../shared/components/link";
import { CreateLinkTo } from "../../../shared/constants/linkTo";
import { useRouteMatch } from 'react-router-dom';
import { IPureTagElementsAfter, MasterTag, IMasterTagTypes, IPureTagAppearance } from '@brg/ui';
import { useGetSelectedTagColor } from "../../../shared/hooks/useGetSelectedTagColor";
import { useSelector } from 'react-redux';
import { TagCategoriesSelector } from 'shared/reducers/Tags';
import { API } from '@brg/api';
import useAppTester from 'shared/MobileMenu/hooks/useAppTester';

/**
 * Свойства Burgundy Tag
 */
export interface BTagProps {
  image?: string | JSX.Element;
  selected?: boolean;
  disabled?: boolean;
  parent?: boolean;
  removable?: boolean;
  secondary?: boolean;
  educational?: boolean;
  organization?: boolean;
  onClick?: (ev: any) => void;
  variant?: string;
  opacity?: boolean;
  isCoauthorTag?: boolean;
  isProjectsTag?: boolean;
  isShowMoreTag?: boolean;
  isInfoTags?: boolean;
  onIconClick?: (ev: any) => void;
  isRequired?: boolean;
  onlyWhiteTags?: boolean;
  isCompetitionsTitleTag?: boolean;
  isEducationCategory?: boolean;
  tag?: any;
  isShort?: boolean;
  isDeziiignStyle?: boolean;
  inactive?: boolean;
  importantColor?: string;
  isTagsCloud?: boolean;
  showRemoveIcon?: boolean;
  hasLink?: boolean;
  isFolder?: boolean;
  isCreateAction?: boolean;
  isCompetitionsStyleTag?: boolean;
  tagCategories?: string;
  icon?: IPureTagElementsAfter;
  noTooltip?: boolean;
  editIconClick?: any;
  colorTheme?: any;
}

/**
 * Burgundy Tag
 *
 * @param image     - изображение если нужно
 * @param children  - название тега (текст)
 * @param selected  - выбран или нет
 * @param disabled  - выключен
 * @param parent    - имеет потомков
 * @param removable - можно удалить
 * @param secondary - вторичный
 * @param onClick   событие при клике
 * @param variant - вариант дизайна
 * @param educational - является ли тег образовательным
 * @param organization - является ли тег тегом организации
 * @param opacity - прозрачность текста
 * @param isProjectsTag - Тег выбранный при редактировании проекта
 * @param isShowMoreTag - Тег в раскрывающемся showmore
 * @param isInfoTags - Теги, которые отображаются в модалке при ховере на табы
 * @param isCoauthorTag - Тег в дропдауне выбора соавторов в проекте
 * @param isRequired - Обязательный тег в данном контексте
 * @param onlyWhiteTags - Все теги должны быть белые
 * @param isCompetitionsTitleTag - тег в названии конкурса
 * @param isEducationCategory - текущая вкладка = "Образовательные теги"
 * @param isShort - тег небольшой
 * @param importantColor - принудительный цвет
 * @param isFolder - тег-папка в профиле
 * @param isCreateAction - кнопка псевдо-тег
 * @param tagCategories - категории к которым принадлежит тег
 * @constructor
 */
export const BTag: FC<React.PropsWithChildren<BTagProps>> = ({
  image,
  children,
  selected,
  disabled,
  removable,
  secondary,
  educational,
  isCreateAction,
  organization,
  onClick,
  variant,
  isCoauthorTag,
  isProjectsTag,
  isShowMoreTag,
  isRequired,
  isFolder,
  isEducationCategory,
  inactive,
  showRemoveIcon,
  onIconClick,
  tag,
  isTagsCloud,
  hasLink,
  tagCategories,
  icon,
  noTooltip,
  editIconClick,
  colorTheme
}) => {
  const { isDarkText } = useTheme();
  const { t, i18n: { language } } = useTranslation();

  const tagCategoriesIndex = useSelector(TagCategoriesSelector);

  const {
    isCompetition,
    isAcademy,
  } = useAppTester();

  const currentCategories = useMemo(() => {
    if (!tagCategories && !!tag?.groupIds?.length && !!tagCategoriesIndex?.size) {
      return tag.groupIds.reduce((acc: string[], groupId: string) => {
        const category = tagCategoriesIndex.get(groupId);
        if (category) {
          acc.push(API.toLang(category, language)?.name || '');
        }
        return acc;
      }, []).join(', ');
    }

    return tagCategories;
  }, [tagCategoriesIndex, tag?.groupIds, tagCategories, language]);

  const educationTip = useCallback(() => {
    const str = [];
    if (isEducationCategory) {
      str.push(t('designer_edit.tips.education_tag'));
    }
    if (isRequired) {
      str.push(t('designer_edit.tips.required_tag'));
    }
    if (!isEducationCategory) {
      str.push(t('designer_edit.tips.main_tag'));
    }

    return str.map((s, idx) => {
      const indent = ` ${s} `;
      return idx > 0 ? indent.toLowerCase() : indent;
    }).join(',');
  }, [isEducationCategory, isRequired, t]);

  const tooltipProps = useMemo(() => {
    if (noTooltip) return {};

    const baseProps = {
      'data-type': isDarkText ? 'dark' : 'light',
      'data-for': 'tooltip-parent-element'
    };

    if (isShowMoreTag) {
      return {
        ...baseProps,
        'data-tip': tag?.name || ''
      };
    }

    if (educational) {
      return {
        ...baseProps,
        'data-tip': currentCategories || educationTip(),
        'data-education': 'true'
      };
    }

    if (organization) {
      return {
        ...baseProps,
        'data-tip': t('designer_edit.tips.team_tag'),
        'data-unit': 'true'
      };
    }

    if (currentCategories || tag?.groupIds?.length > 0) {
      return {
        ...baseProps,
        'data-tip': currentCategories,
        'data-default': 'true'
      };
    }

    if (isFolder && isCreateAction) {
      return {
        ...baseProps,
        'data-tip': t('designer_edit.tips.team_tag'),
        'data-unit': 'true'
      };
    }

    return {};
  }, [noTooltip, isDarkText, isShowMoreTag, tag?.name, educational, currentCategories, educationTip, organization, t, tag?.groupIds, isFolder, isCreateAction]);

  const selectedColor = useGetSelectedTagColor(isFolder || false);
  const isFilmRoute = !!useRouteMatch('/film');

  const to = useMemo(() => {
    if (isFilmRoute) {
      return '/film' + CreateLinkTo.tag(tag?.tagId);
    }
    return CreateLinkTo.tag(tag?.tagId);
  }, [isFilmRoute, tag?.tagId]);

  const elementAfter = useMemo(() => {
    if (icon) {
      return { type: IPureTagElementsAfter[icon], extraProps: { onClick: onIconClick } };
    }

    if (showRemoveIcon) {
      return { type: IPureTagElementsAfter.close, extraProps: { onClick: onIconClick } };
    }

    return undefined;
  }, [icon, showRemoveIcon, onIconClick]);

  const masterTagType = useMemo(() => {
    if (educational) {
      return IMasterTagTypes.educational;
    }
    if (organization) {
      return IMasterTagTypes.organization;
    }
    if (isFolder) {
      return IMasterTagTypes.folder;
    }
    return IMasterTagTypes.simple;
  }, [educational, organization, isFolder]);

  if (image) {
    return (
      <TagItemViewWrapper
        {...tooltipProps}
        secondary={secondary}
        onClick={onClick}
        selected={selected}
        disabled={disabled}
        removable={removable}
        isCoauthorTag={isCoauthorTag}
        variant={variant}
        educational={educational}
        organization={organization}
        inactive={inactive}
        isAcademy={isAcademy}
        isCompetition={isCompetition}
        isTagsCloud={isTagsCloud}
      >
        {typeof image === 'string' ? (
          <TagItemViewImage image={image} />
        ) : (
          <TagItemViewImage image="">{image}</TagItemViewImage>
        )}
        {children}
      </TagItemViewWrapper>
    );
  }

  return (
    <Link to={to} disabled={!tag?.tagId || !hasLink} type="tag">
      <MasterTag
        {...tooltipProps}
        onClick={onClick}
        active={selected}
        disabled={disabled}
        type={masterTagType}
        elementAfter={elementAfter}
        activeColorTheme={selectedColor}
        editIconClick={editIconClick}
        {...(colorTheme ? { colorTheme, appearance: IPureTagAppearance.filled } : {})}
      >
        {children}
      </MasterTag>
    </Link>
  );
};
